import * as R from 'ramda';
import { put, call, select, takeLatest } from 'redux-saga/effects';
// components
import { closeLoader, openLoader } from '../../../components/loader/actions';
import { transformSearchCriteriaBeforeReportPost } from '../../../components/edit-report/helpers';
// features
import { makeSelectCurrentBranchGuid } from '../../branch/selectors';
import { getGeofencingZoneColorConfigRequest } from '../../configurations/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// report-common
import { getReportSagas } from '../../../report-common';
// sagas
import { crudSaga, visitPageSaga } from '../../../sagas';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature geo-fencing-zone
import * as A from './actions';
import * as S from './selectors';
//////////////////////////////////////////////////

export function* handleGetItemListSaga({ payload }) {
  try {
    if (G.isTrue(G.getPropFromObject('openLoader', payload))) {
      yield put(openLoader({ showDimmer: true }));
    }

    yield put(A.setListLoading(true));

    const reportParams = yield select(S.makeSelectUsedReport());
    const availableReports = yield select(S.makeSelectAvailableReports());

    if (R.and(
      G.isNilOrEmpty(availableReports),
      G.isNilOrEmpty(G.getPropFromObject('fields', reportParams)),
    )) {
      yield put(A.setListLoading(false));

      return yield put(closeLoader());
    }

    const pagination = yield select(S.makeSelectPagination());
    const titleOrderFields = yield select(S.makeSelectTitleSortValues());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const titleFilterParams = yield select(S.makeSelectTableTitleFilters());

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const guids = R.pathOr(null, ['guids'], payload);

    const { limit, offset } = pagination;

    const reqBody = {
      guids,
      limit,
      offset,
      orderFields,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      fields: G.getOrElse(reportParams, 'fields', []),
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
      systemFields: [
        { sequence: 10, name: GC.FIELD_NAME },
        { sequence: 11, name: GC.FIELD_COLOR },
        { sequence: 12, name: GC.FIELD_VERSION },
        { sequence: 13, name: GC.FIELD_GEO_FENCING_ZONE_ZONE_USAGE },
        { sequence: 14, collection: true, name: GC.FIELD_GEO_FENCING_ZONE_ZONE_ADDRESS_POINTS },
      ],
    };

    const res = yield call(sendRequest, 'post', endpointsMap.reportGeoFencingZoneList, { data: reqBody });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess({ data, guids }));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetItemListSaga fail');
    }

    yield put(A.setListLoading(false));
    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield put(A.setListLoading(false));

    yield call(G.handleException, error, 'handleGetItemListSaga exception');
  }
}

export function* createOrUpdateGeoFencingZoneRequest({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const method = G.ifElse(
      G.isNilOrEmpty(payload.version),
      'post',
      'put',
    );

    const options = {
      data: R.assoc(GC.BRANCH_GUID, branchGuid, payload),
    };

    yield call(crudSaga, {
      method,
      options,
      shouldCloseModal: true,
      showSuccessMessage: true,
      successMessage: 'messages:success:200-201',
      endpoint: endpointsMap.reportGeoFencingZone,
      parentSagaName: 'createOrUpdateGeoFencingZoneRequest',
      successAction: (data) => A.getItemListRequest({ guids: R.of(Array, G.getGuidFromObject(data)) }),
    });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'createOrUpdateGeoFencingZoneRequest exception');
  }
}

export function* deleteGeoFencingZoneRequest({ payload }) {
  try {
    yield put(openLoader());

    const endpoint = endpointsMap.reportGeoFencingZoneByGuid(payload);

    yield call(crudSaga, {
      payload,
      endpoint,
      method: 'delete',
      shouldCloseModal: true,
      showSuccessMessage: true,
      successAction: A.deleteItemSuccess,
      successMessage: 'messages:success:204',
      parentSagaName: 'deleteGeoFencingZoneRequest',
    });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'deleteGeoFencingZoneRequest exception');
  }
}

export function* createGeoFencingZoneExportRequest() {
  try {
    yield put(openLoader({ showDimmer: true }));

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      resType: 'arraybuffer',
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.exportReportGeoFencingZone, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      G.saveFileFromResponse(res);
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'createGeoFencingZoneExportRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'createGeoFencingZoneExportRequest exception');
  }
}

export function* getAvailableReportGeoFencingZoneList() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    yield call(crudSaga, {
      options,
      method: 'get',
      endpoint: endpointsMap.availableReportGeoFencingZone,
      parentSagaName: 'getAvailableReportGeoFencingZoneList',
      successAction: A.getAvailableReportGeoFencingZoneListSuccess,
    });
  } catch (error) {
    yield call(G.handleException, error, 'getAvailableReportGeoFencingZoneList exception');
  }
}

const createUpdateReportSuccessCallback = (data) => G.getReportSortedBySeqFreez(data);

const {
  handleAvailableReportsRequest,
  handleCreateReportRequestSaga,
  handleUpdateReportRequestSaga,
  handleChangeDefaultReportSaga,
} = getReportSagas(
  GC.REPORT_GEO_FENCING_ZONE_REPORT,
  A,
  handleGetItemListSaga,
  { S, createUpdateReportSuccessCallback },
);

export function* handleVisitGeoFencingZoneSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_REPORT_GEO_FENCING_ZONE_PAGE);

    yield put(openLoader());

    const pageVisited = yield select(S.makeSelectPageVisited());

    yield put(A.setInitialStateOmitReport(['availableList']));

    yield put(A.setIgnorePromptStatus(false));
    yield put(A.setReportPending());

    yield call(handleAvailableReportsRequest, { payload, notSetUsedReport: pageVisited });
    yield call(handleGetItemListSaga, { payload: { openLoader: true } });

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    yield put(getGeofencingZoneColorConfigRequest(branchGuid));

    yield put(closeLoader());

    break;
  }
}

function* geoFencingZoneWatcherSaga() {
  // report
  yield takeLatest(A.getItemListRequest, handleGetItemListSaga);
  yield takeLatest(A.createReportRequest, handleCreateReportRequestSaga);
  yield takeLatest(A.updateReportRequest, handleUpdateReportRequestSaga);
  yield takeLatest(A.changeDefaultReportRequest, handleChangeDefaultReportSaga);
  // geo fencing zone
  yield takeLatest(A.deleteItemRequest, deleteGeoFencingZoneRequest);
  yield takeLatest(GC.VISIT_REPORT_GEO_FENCING_ZONE_PAGE, handleVisitGeoFencingZoneSaga);
  yield takeLatest(A.createGeoFencingZoneExportRequest, createGeoFencingZoneExportRequest);
  yield takeLatest(A.createOrUpdateGeoFencingZoneRequest, createOrUpdateGeoFencingZoneRequest);
  yield takeLatest(A.getAvailableReportGeoFencingZoneListRequest, getAvailableReportGeoFencingZoneList);
}

export default geoFencingZoneWatcherSaga;
